/**
 * Chat reads and writes on the relay: the session transcript, durable admissions, the native run, and the run's
 * event stream. The chat snapshot the views fold is synthesized here from those reads; nothing is stored.
 */
import { z } from 'zod'
import { chatHistoryPageSize, isTerminalRun, nativeMessageSchema, nativeRunSchema } from '../hermes/chat-schema'
import type { ResearchTask } from './research-schema'
import { relayStreamError } from './contract'
import { op, isUserMessage, type UserMessage } from './agent-contract'
export { op } from './agent-contract'
import type { OpsClient } from './ops'

export type SessionScope =
  | { scope: 'coworld'; coworld: string; league: string }
  | { scope: 'workspace'; coworld: null; league: null }
export const openedSession = z.object({ session_id: z.string() })

export const sessionId = /^[A-Za-z0-9_-]{1,200}$/
/** A browser projection of native inputs, not an IDE database request or conversation row. */
const chatSnapshotSchema = z.object({
  conversationId: z.string().min(1).max(200),
  messages: z.array(nativeMessageSchema),
  requests: z.array(
    z.object({
      request_id: z.uuid(),
      request: z.object({ message: z.custom<UserMessage>(isUserMessage) }),
      run_id: z.string().nullable(),
      terminal: nativeRunSchema.extend({ run_id: z.string().nullable() }).nullable(),
      /** Set while the workspace holds the request after a budget, rate-limit or provider failure; it resumes itself. */
      waiting: z.object({ error_class: z.string().nullable(), resume_after: z.string().nullable() }).nullable(),
      created_at: z.string(),
    })
  ),
  active: nativeRunSchema.nullable(),
  hasOlderMessages: z.boolean(),
})
export type ChatSnapshot = z.infer<typeof chatSnapshotSchema>
const timestamp = z.union([z.string(), z.number()]).nullish()
/** `sessions.list`: the Hermes session listing. Child sessions belong to delegated research, not the thread list. */
export const sessionList = z.object({
  data: z.array(
    z.object({
      id: z.string(),
      title: z.string().nullish(),
      created_at: timestamp,
      updated_at: timestamp,
      parent_session_id: z.string().nullish(),
    })
  ),
})
export type SessionRow = z.infer<typeof sessionList>['data'][number]
const sessionMessages = z.object({ data: z.array(nativeMessageSchema) })
/** Native workspace state includes durable admissions and registered sessions. */
const workspaceSnapshot = z.object({
  seq: z.number(),
  records: z.object({
    admissions: z.array(
      z.object({
        op_id: z.uuid(),
        session_id: z.string(),
        message: z.string(),
        input: z.custom<UserMessage>(isUserMessage),
        status: z.enum(['queued', 'running', 'waiting', 'completed', 'cancelled', 'failed', 'interrupted']),
        run_id: z.string().nullable(),
        error: z.string().nullable(),
        // Older workspace images omit both. The class stays a string so a new class does not break the snapshot.
        error_class: z.string().nullish(),
        resume_after: z.string().nullish(),
        created_at: z.string(),
      })
    ),
    sessions: z.array(
      z.object({
        id: z.string(),
        run_id: z.string().nullable(),
        started: z.string(),
      })
    ),
  }),
})
const taskSnapshot = z.object({
  coworld_id: z.string(),
  records: z.object({
    tasks: z.array(
      z.object({
        id: z.string().regex(/^T-[1-9]\d*$/),
        title: z.string(),
        for_human: z.string(),
        description: z.string(),
        status: z.enum(['proposed', 'unclaimed', 'in-progress', 'complete', 'abandoned']),
      })
    ),
  }),
})
export const runEvent = z.object({ event: z.string(), run_id: z.string(), seq: z.number().optional() }).loose()
export type RunEvent = z.infer<typeof runEvent>
export const TERMINAL_EVENTS = new Set(['run.completed', 'run.cancelled', 'run.failed', 'run.interrupted'])
/** Admission statuses that are still the conversation's open request. A `waiting` one resumes without the client. */
const OPEN_ADMISSIONS = new Set(['queued', 'running', 'waiting'])

/** What the composer says while the workspace holds a request, with the resume time in the viewer's local time. */
export function waitingActivity(waiting: { error_class: string | null; resume_after: string | null }) {
  const resumeAt = waiting.resume_after ? new Date(waiting.resume_after) : null
  const time =
    resumeAt && !Number.isNaN(resumeAt.getTime())
      ? resumeAt.toLocaleString(undefined, {
          ...(resumeAt.toDateString() === new Date().toDateString() ? {} : { weekday: 'short' }),
          hour: 'numeric',
          minute: '2-digit',
        })
      : null
  switch (waiting.error_class) {
    case 'budget_exhausted':
      return time
        ? `Out of model budget until ${time}. Your request resumes on its own.`
        : 'Out of model budget. Your request resumes on its own.'
    case 'rate_limited':
      return time ? `The model provider is busy. Retrying after ${time}.` : 'The model provider is busy. Retrying soon.'
    case 'provider_unavailable':
      return time ? `The model provider is down. Retrying after ${time}.` : 'The model provider is down. Retrying soon.'
    default:
      return time ? `Your request is waiting. Retrying after ${time}.` : 'Your request is waiting. Retrying soon.'
  }
}

export const iso = (value: string | number | null | undefined) =>
  value === null || value === undefined
    ? new Date(0).toISOString()
    : typeof value === 'number'
      ? new Date(value * 1000).toISOString()
      : value

/** Registered native chat sessions; task and wake sessions are not browser conversations. */
export async function listSessions(
  ops: OpsClient,
  workspaceId: string,
  coworldId: string,
  leagueId: string,
  signal?: AbortSignal
): Promise<SessionRow[]> {
  const snapshot = await readWorkspaceSnapshot(ops, workspaceId, { coworld: coworldId, league: leagueId }, signal)
  return snapshot.records.sessions.map((session) => ({
    id: session.id,
    created_at: session.started,
  }))
}

/** The runtime selects the session records and admissions for the requested IDs. */
function readWorkspaceSnapshot(
  ops: OpsClient,
  workspaceId: string,
  selection: { coworld: string | null; league: string | null; session_id?: string },
  signal?: AbortSignal
) {
  return ops.json(
    workspaceId,
    op({ name: 'workspace.snapshot', args: { store: 'workspace', ...selection } }),
    workspaceSnapshot,
    signal
  )
}

/** Combine native transcript, admission state, and the current run for the chat view. */
export async function readSnapshot(
  ops: OpsClient,
  workspaceId: string,
  session: string,
  scope: SessionScope,
  offset = 0,
  signal?: AbortSignal
): Promise<ChatSnapshot> {
  const [history, workspace] = await Promise.all([
    ops.json(
      workspaceId,
      op({
        name: 'session.messages',
        args: { session_id: session, coworld: scope.coworld, league: scope.league, offset, limit: chatHistoryPageSize },
      }),
      sessionMessages,
      signal
    ),
    readWorkspaceSnapshot(
      ops,
      workspaceId,
      { session_id: session, coworld: scope.coworld, league: scope.league },
      signal
    ),
  ])
  const registered = workspace.records.sessions[0]!
  const admissions = workspace.records.admissions
  const open = admissions.findLast((row) => OPEN_ADMISSIONS.has(row.status))
  // A waiting admission's run_id names the run that failed; there is no current run until it resumes.
  const runId = open ? (open.status === 'waiting' ? null : open.run_id) : registered.run_id
  const run = runId
    ? await ops.json(workspaceId, op({ name: 'run.get', args: { run_id: runId } }), nativeRunSchema, signal)
    : null
  return chatSnapshotSchema.parse({
    conversationId: session,
    messages: history.data,
    requests: admissions.map((row) => ({
      request_id: row.op_id,
      request: { message: row.input },
      run_id: row.run_id,
      terminal: OPEN_ADMISSIONS.has(row.status)
        ? null
        : {
            run_id: row.run_id,
            status: row.status,
            ...(row.error ? { error: row.error } : {}),
          },
      waiting:
        row.status === 'waiting'
          ? { error_class: row.error_class ?? null, resume_after: row.resume_after ?? null }
          : null,
      created_at: row.created_at,
    })),
    active: run && !isTerminalRun(run) ? run : null,
    hasOlderMessages: history.data.length === chatHistoryPageSize,
  })
}

export async function activeRunId(ops: OpsClient, workspaceId: string, session: string, scope: SessionScope) {
  const snapshot = await readSnapshot(ops, workspaceId, session, scope)
  return snapshot.active?.run_id ?? null
}

/** Read only the selected Coworld's board, independently of other sessions. */
export async function researchList(
  ops: OpsClient,
  workspaceId: string,
  coworldId: string,
  offset: number,
  signal?: AbortSignal
) {
  const snapshot = await ops.json(
    workspaceId,
    op({ name: 'workspace.snapshot', args: { store: 'coworld', coworld: coworldId } }),
    taskSnapshot,
    signal
  )
  const tasks: ResearchTask[] = snapshot.records.tasks.map((task) => ({
    task_id: task.id,
    display_id: Number(task.id.slice(2)),
    title: task.title,
    status: task.status,
    native_session_id: null,
    workspace: snapshot.coworld_id,
    summary: task.for_human || task.description,
  }))
  return { tasks: tasks.slice(offset, offset + 50), has_more: tasks.length > offset + 50 }
}

/** `data:` payloads of a server-sent event stream, with their `event:` name, until the stream ends or `signal` aborts. */
export async function* sseFrames(body: ReadableStream<Uint8Array>, signal: AbortSignal) {
  const reader = body.getReader()
  const decoder = new TextDecoder()
  const cancel = () => void reader.cancel().catch(() => {})
  signal.addEventListener('abort', cancel, { once: true })
  let buffer = ''
  try {
    while (true) {
      const { value, done } = await reader.read()
      if (done) return
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n')
      let boundary = buffer.indexOf('\n\n')
      while (boundary >= 0) {
        const lines = buffer.slice(0, boundary).split('\n')
        buffer = buffer.slice(boundary + 2)
        const data = lines
          .filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).trimStart())
          .join('\n')
        const event =
          lines
            .find((line) => line.startsWith('event:'))
            ?.slice(6)
            .trim() ?? 'message'
        if (data) yield { event, data }
        boundary = buffer.indexOf('\n\n')
      }
    }
  } finally {
    signal.removeEventListener('abort', cancel)
    reader.releaseLock()
  }
}

/**
 * The native events of one run, from `last_seq` on. Yields parsed events; ends at the run's terminal event. A dead
 * stream or an in-band `relay_error` ends the iteration early with `{ ended: 'lost', lastSeq }`, so the caller falls
 * back to `run.get` and `session.messages` and may reconnect from `lastSeq`.
 */
export async function followRun(
  ops: OpsClient,
  workspaceId: string,
  runId: string,
  lastSeq: number,
  signal: AbortSignal,
  onEvent: (event: RunEvent) => void
): Promise<{ ended: 'terminal' | 'lost'; lastSeq: number }> {
  const response = await ops.stream(
    workspaceId,
    op({ name: 'run.events', args: { run_id: runId, last_seq: lastSeq } }),
    signal
  )
  for await (const frame of sseFrames(response.body!, signal)) {
    if (frame.event === 'relay_error') {
      relayStreamError.parse(JSON.parse(frame.data))
      return { ended: 'lost', lastSeq }
    }
    const event = runEvent.parse(JSON.parse(frame.data))
    if (event.run_id !== runId) continue
    if (event.seq !== undefined) {
      if (event.seq <= lastSeq) continue
      lastSeq = event.seq
    }
    onEvent(event)
    if (TERMINAL_EVENTS.has(event.event)) return { ended: 'terminal', lastSeq }
  }
  return { ended: 'lost', lastSeq }
}
